import React, { useCallback, useState } from "react";

import CustomButton from "components/common/Button";
import InlineLoader from "./InlineLoader";
import { listBillingRefunds } from "services/billing";
import type { BsPaginated } from "services/billing";

import CustomerFilter from "./CustomerFilter";
import FilterSelect from "./FilterSelect";
import { useCursorList } from "./useCursorList";
import { CustomChip, TableComponent } from "./ui";
import { BsRefund, REFUND_STATUS_OPTIONS, formatDate, formatMoney, statusColor, titleCase } from "./types";

const columns = [
  {
    id: "status",
    label: "Status",
    render: (v: string) => <CustomChip label={titleCase(v)} size="small" color={statusColor(v)} />,
  },
  { id: "amountCents", label: "Amount", render: (v: number, row: BsRefund) => formatMoney(v, row.currency) },
  { id: "reason", label: "Reason", render: (v: string) => titleCase(v) },
  { id: "gatewayRefundId", label: "Gateway ref", render: (v: string) => v || "—" },
  { id: "createdAt", label: "Created", render: (v: string) => formatDate(v) },
];

export default function RefundsTab() {
  // [PRD-3791] Customer and status filters.
  const [customerId, setCustomerId] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState("");
  const load = useCallback(
    (cursor?: string) =>
      listBillingRefunds({ customerId, status: status || undefined, cursor, limit: 25 }) as Promise<
        BsPaginated<BsRefund>
      >,
    [customerId, status]
  );
  const { rows, hasMore, loading, loadingMore, error, loadMore } = useCursorList<BsRefund>(load);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex gap-1 items-end" style={{ flexWrap: "wrap" }}>
        <CustomerFilter onChange={setCustomerId} />
        <FilterSelect name="refunds-status" label="Status" options={REFUND_STATUS_OPTIONS} value={status} onChange={setStatus} />
      </div>
      {loading ? (
        <InlineLoader />
      ) : error ? (
        <p className="para-body-m-regular color-text-body-secondary">{error}</p>
      ) : (
        <>
          <TableComponent columns={columns} data={rows} disableSort />
          {hasMore && (
            <div className="flex justify-center mt-05">
              <CustomButton label={loadingMore ? "Loading…" : "Load more"} onClick={loadMore} disabled={loadingMore} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
